// ─────────────────────────────────────────────────────────────────────
//  RBAC permission catalog — the agent-console's copy of the TypeScript
//  mirror that admin-portal already carries.
//
//  Until the Spam / unwanted action (OP-1005) the agent-console had NO
//  client-side permission check at all: every staff action was gated only by
//  the server. The action that ends in a permanent delete must not be OFFERED
//  to a user the server will refuse, so the console now carries the same
//  matrix. It is a COPY, not a new design.
//
//  LOCKSTEP — this file MUST stay in sync with
//    Shared/CustomerSupportChatSystem.Infrastructure/Authorization/Permissions.cs
//    Frontend/admin-portal/src/auth/permissions.ts
//  The role -> permission matrix and the role canonicalization are the
//  contract; they are pinned by __tests__/auth/permissions.pin.test.ts. Do
//  NOT rename a string or change a row here without the backend + the
//  admin-portal mirror.
//
//  The UI check is UX, never security: the server evaluates the same matrix
//  per request, and a hidden button is not an authorization.
// ─────────────────────────────────────────────────────────────────────

/** A fine-grained capability string. Mirrors Permissions.cs consts. */
export type Permission =
  | 'tenants.manage'
  | 'users.manage'
  | 'bots.manage'
  | 'config.edit'
  | 'tenantMind.view'
  | 'tenantMind.edit'
  | 'knowledge.manage'
  | 'learning.review'
  | 'memory.manage'
  | 'admin:agent_learning_management'
  | 'analytics.view'
  | 'analytics.questions.view'
  | 'analytics.cost.view'
  | 'dashboard.view'
  | 'logs.view'
  | 'diagnostics.view'
  | 'workspace.view'
  | 'workspace.handle'
  | 'workspace.viewAll';

/** The four MAIN portal roles. `developer` is an additive overlay, not a main role. */
export type MainRole = 'platformAdmin' | 'admin' | 'seniorAgent' | 'agent';

/**
 * The full role -> permission matrix. A user's EFFECTIVE permission set is the UNION of the rows
 * for every role they hold (roles are additive). Keys are CANONICAL role names; look up only
 * after {@link canonicalizeRole}. Mirrors Permissions.RolePermissions in C#.
 */
export const ROLE_PERMISSIONS: Readonly<Record<MainRole, readonly Permission[]>> = {
  platformAdmin: [
    'tenants.manage',
    'users.manage',
    'bots.manage',
    'config.edit',
    'tenantMind.view',
    'tenantMind.edit',
    'knowledge.manage',
    'learning.review',
    'memory.manage',
    'admin:agent_learning_management',
    'analytics.view',
    'analytics.questions.view',
    'analytics.cost.view',
    'dashboard.view',
    'logs.view',
    'diagnostics.view',
    'workspace.view',
    'workspace.handle',
    'workspace.viewAll',
  ],
  admin: [
    'users.manage',
    'bots.manage',
    'config.edit',
    'tenantMind.view',
    'tenantMind.edit',
    'knowledge.manage',
    'learning.review',
    'memory.manage',
    'analytics.view',
    'analytics.questions.view',
    'dashboard.view',
    'logs.view',
    'diagnostics.view',
    'workspace.view',
    'workspace.handle',
    'workspace.viewAll',
  ],
  seniorAgent: [
    'dashboard.view',
    'analytics.view',
    'tenantMind.view',
    'tenantMind.edit',
    'knowledge.manage',
    'learning.review',
    'workspace.view',
    'workspace.handle',
    'workspace.viewAll',
  ],
  agent: [
    'dashboard.view',
    'analytics.view',
    'tenantMind.view',
    'workspace.view',
    'workspace.handle',
  ],
} as const;

/** The additive `developer` overlay — logs + diagnostics only. Mirrors the Developer row in C#. */
export const DEVELOPER_PERMISSIONS: readonly Permission[] = ['logs.view', 'diagnostics.view'] as const;

/**
 * Canonical form of a raw role string. Case-insensitive; legacy underscore / concatenated / alias
 * forms map onto the canonical name. An unknown role passes through UNCHANGED so it contributes
 * no permissions downstream. Mirrors RoleNormalizer.Canonicalize.
 */
export function canonicalizeRole(role: string): string {
  if (role == null) return role;
  const trimmed = role.trim();
  if (trimmed.length === 0) return role;

  switch (trimmed.toLowerCase()) {
    case 'platformadmin':
    case 'platform_admin':
      return 'platformAdmin';

    case 'tenantadmin':
    case 'tenant_admin':
    case 'admin':
      return 'admin';

    case 'senioragent':
    case 'senior_agent':
      return 'seniorAgent';

    case 'agent':
      return 'agent';

    case 'developer':
      return 'developer';

    default:
      return role;
  }
}

/**
 * The effective permission set (UNION across every held role, developer overlay additive) for a
 * raw role-claim array. Mirrors Permissions.PermissionsForRoles. Unknown roles — and `customer`,
 * and an empty list — contribute nothing, i.e. FAIL CLOSED.
 */
export function permissionsForRoles(roles: readonly string[]): Set<Permission> {
  const effective = new Set<Permission>();
  for (const raw of roles ?? []) {
    const canonical = canonicalizeRole(raw);
    if (canonical === 'developer') {
      for (const p of DEVELOPER_PERMISSIONS) effective.add(p);
      continue;
    }
    // Own-property check, not a bare lookup: a role string such as "constructor" would otherwise
    // resolve to an Object.prototype member, which is truthy and not iterable.
    if (Object.prototype.hasOwnProperty.call(ROLE_PERMISSIONS, canonical)) {
      for (const p of ROLE_PERMISSIONS[canonical as MainRole]) effective.add(p);
    }
  }
  return effective;
}
